/**
 * The platform error envelope (design/07 §1) → one `ApiError` the screens understand.
 *
 * `{ error: { code, message, details[], requestId } }` — `code` is stable (the UI text comes from
 * i18n `errors.<code>`), `message` is already translated by the service (`Accept-Language`) and is
 * the fallback. A failed network call becomes `code: "network"` (the tablet is offline / the server
 * is unreachable) so every screen can say so in plain words.
 */

export type ErrorDetail = Record<string, unknown>;

export interface ErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
    requestId?: string;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ErrorDetail[];
  readonly requestId: string | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details: ErrorDetail[] = [],
    requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** First detail object (most errors carry one). */
  get detail(): ErrorDetail {
    return this.details[0] ?? {};
  }

  static fromEnvelope(status: number, body: unknown): ApiError {
    const envelope = (typeof body === "object" && body !== null ? body : {}) as ErrorEnvelope;
    const error = envelope.error ?? {};
    const raw = error.details;
    const details: ErrorDetail[] = Array.isArray(raw)
      ? raw.filter((d): d is ErrorDetail => typeof d === "object" && d !== null)
      : typeof raw === "object" && raw !== null
        ? [raw as ErrorDetail]
        : [];
    return new ApiError(
      status,
      error.code ?? (status === 0 ? "network" : `http_${String(status)}`),
      error.message ?? "",
      details,
      error.requestId,
    );
  }

  static network(cause?: unknown): ApiError {
    const error = new ApiError(0, "network", cause instanceof Error ? cause.message : "network");
    return error;
  }
}

export function isApiError(value: unknown, code?: string): value is ApiError {
  return value instanceof ApiError && (code === undefined || value.code === code);
}

/**
 * openapi-fetch result → data or throw `ApiError`. Every POS call goes through this, so the
 * screens only deal with `ApiError` (never with raw responses).
 */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || result.data === undefined) {
    if (result.response.ok && result.error === undefined) {
      // 204 / empty body
      return undefined as T;
    }
    throw ApiError.fromEnvelope(result.response.status, result.error);
  }
  return result.data;
}

/**
 * What the cashier reads for an error (T07-36 review 2026-09-28): `errors.<code>_<reason>` when the
 * reason has its own sentence (a card not on sale yet is not "sold"), else `errors.<code>` — every
 * code pos-service answers has one in ja / th / en (parity: services/pos
 * tests/unit/test_error_catalog.py). Only a code the app does not know falls back to the server's
 * message, which pos-service already translated (`Accept-Language`) into one plain sentence.
 */
/** Whole minutes left from `retryAfter` (seconds) or `lockedUntil` (ISO time) of an error. */
export function minutesOf(
  detail: Record<string, unknown>,
  now: number = Date.now(),
): number | null {
  const retry = detail.retryAfter;
  let seconds: number | null = null;
  if (typeof retry === "number" && Number.isFinite(retry)) {
    seconds = retry;
  } else if (typeof retry === "string" && /^\d+$/.test(retry)) {
    seconds = Number(retry);
  } else if (typeof detail.lockedUntil === "string") {
    const until = Date.parse(detail.lockedUntil);
    seconds = Number.isNaN(until) ? null : (until - now) / 1000;
  }
  return seconds === null ? null : Math.max(1, Math.ceil(seconds / 60));
}

export function errorText(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (error instanceof ApiError) {
    const minutes = minutesOf(error.detail);
    if (error.code === "account_locked" && minutes !== null) {
      // identity's lock time (PIN: 10 min) — from the answer, never a number written here
      return t("errors.account_locked_in", { minutes });
    }
    const reason = error.detail.reason;
    if (typeof reason === "string" && /^[a-z_]+$/.test(reason)) {
      const byReason = t(`errors.${error.code}_${reason}`, { ...error.detail, defaultValue: "" });
      if (byReason) {
        return byReason;
      }
    }
    const text = t(`errors.${error.code}`, { ...error.detail, defaultValue: "" });
    if (text) {
      return text;
    }
    if (error.message) {
      return error.message;
    }
    return t("errors.unknown");
  }
  return t("errors.unknown");
}
